// src/NewUPT/shared/BodyImageMap.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './BodyImageMap.module.css';

// FRONT VIEW REGIONS
const frontRegions = [
  // ==== HEAD & NECK ====
  {
    id: 'front-head',
    label: 'Head (Skull)',
    muscles: ['Head – skull region'],
    top: '6%',
    left: '26%',
    route: '/pain/head',
  },
  
  {
    id: 'front-neck',
    label: 'Neck / Cervical Spine',
    muscles: [
      'Neck / Cervical Spine – upper spine',
      'Scalenes – side of the neck',
      'Sternocleidomastoid (SCM) – front/side of neck',
    ],
    top: '17%',
    left: '23%',
    route: '/pain/neckCervicalSpine',
  },

  // ==== SHOULDER REGION ====
  {
    id: 'front-shoulder-region',
    label: 'Shoulder Region',
    muscles: [
      'Shoulder Joint (GH joint)',
      'Rotator Cuff (Supraspinatus, Infraspinatus, Teres Minor, Subscapularis)',
      'Deltoid – outer shoulder',
      'Upper Trapezius – top of shoulders/neck',
    ],
    top: '20%',
    left: '37%',
    route: '/pain/shoulderRegion',
  },

  // ==== UPPER ARM ====
  {
    id: 'front-upper-arm',
    label: 'Upper Arm',
    muscles: [
      'Biceps Brachii – front of upper arm',
      'Brachialis – deep front of upper arm',
    ],
    top: '29%',
    left: '15%',
    route: '/pain/upperArm',
  },

  // ==== ELBOW & FOREARM ====
  {
    id: 'front-elbow-forearm',
    label: 'Elbow & Forearm',
    muscles: [
      'Elbow Joint – between upper arm & forearm',
      'Forearm Flexors – inner forearm',
      'Forearm Extensors – outer forearm',
      'Brachioradialis – top of forearm',
    ],
    top: '34%',
    left: '38%',
    route: '/pain/elbowForearm',
  },

  // ==== WRIST & HAND ====
  {
    id: 'front-wrist-hand',
    label: 'Wrist & Hand',
    muscles: [
      'Wrist Joint – base of the hand',
      'Carpal Area – palm base',
      'Finger Flexors – inside forearm to palm',
      'Finger Extensors – top of hand/forearm',
      'Thumb Muscles (Thenar group) – palm thumb side',
    ],
    top: '44%',
    left: '10%',
    route: '/pain/wristHand',
  },

  // ==== CHEST ====
  {
    id: 'front-chest',
    label: 'Chest',
    muscles: [
      'Pectoralis Major – front of chest',
      'Pectoralis Minor – deep chest',
    ],
    top: '25%',
    left: '30%',
    route: '/pain/chest',
  },

  // ==== ABDOMEN / CORE ====
  {
    id: 'front-abdomen-core',
    label: 'Abdomen / Core',
    muscles: [
      'Rectus Abdominis – front abs',
      'Obliques (Internal & External) – sides of torso',
      'Transverse Abdominis – deep core',
    ],
    top: '38%',
    left: '25%',
    route: '/pain/abdomenCore',
  },

  // ==== PELVIS & HIPS (FRONT) ====
  {
    id: 'front-pelvis-hips',
    label: 'Pelvis & Hips (Front)',
    muscles: [
      'Pelvic Floor Muscles – base of pelvis',
      'Hip Joint – upper thigh',
      'Iliopsoas – front of hip',
      'TFL (Tensor Fasciae Latae) – outer front hip',
    ],
    top: '40%',
    left: '33%',
    route: '/pain/pelvisHipsFront',
  },

  // ==== THIGH (FRONT) ====
  {
    id: 'front-thigh',
    label: 'Front Thigh (Quads & Hip Flexors)',
    muscles: [
      'Quadriceps Group (Rectus Femoris, Vastus Lateralis, Vastus Medialis, Vastus Intermedius)',
      'Hip Flexors – front upper thigh',
      'IT Band – outer thigh',
    ],
    top: '53%',
    left: '31%',
    route: '/pain/thighFront',
  },

  // ==== INNER THIGH ====
  {
    id: 'front-inner-thigh',
    label: 'Inner Thigh (Adductors)',
    muscles: [
      'Adductor Longus',
      'Adductor Magnus',
      'Adductor Brevis',
      'Gracilis',
    ],
    top: '58%',
    left: '24%',
    route: '/pain/innerThigh',
  },

  // ==== KNEE (FRONT) ====
  {
    id: 'front-knee',
    label: 'Knee (Front)',
    muscles: [
      'Knee Joint – front/middle of leg',
      'Patella – kneecap',
      'Patellar Tendon – below kneecap',
      'Meniscus – inside knee',
    ],
    top: '66%',
    left: '30%',
    route: '/pain/kneeFront',
  },

  // ==== LOWER LEG (FRONT) ====
  {
    id: 'front-lower-leg',
    label: 'Lower Leg (Front & Outer)',
    muscles: [
      'Tibialis Anterior – front shin',
      'Fibularis (Peroneals) – outer lower leg',
    ],
    top: '78%',
    left: '21%',
    route: '/pain/lowerLegFront',
  },

  // ==== ANKLE & FOOT (FRONT/BOTTOM) ====
  {
    id: 'front-ankle-foot',
    label: 'Ankle & Foot',
    muscles: [
      'Ankle Joint – top of foot',
      'Plantar Fascia – bottom of foot',
      'Foot Arch Muscles',
      'Toes / Intrinsic Foot Muscles',
    ],
    top: '87%',
    left: '30%',
    route: '/pain/ankleFoot',
  },
];

// BACK VIEW REGIONS
const backRegions = [
  // ==== HEAD & NECK (BACK) ====
  {
    id: 'back-head-neck',
    label: 'Head & Neck (Back)',
    muscles: [
      'Neck / Cervical Spine – upper spine',
      'Upper Trapezius – top of shoulders/neck',
      'Levator Scapulae – side/back of neck',
    ],
    top: '15%',
    left: '70%',
    route: '/pain/headNeckBack',
  },

  // ==== UPPER BACK / SHOULDER GIRDLE ====
  {
    id: 'back-upper-back',
    label: 'Upper Back',
    muscles: [
      'Thoracic Spine – mid-back',
      'Middle Trapezius – between shoulder blades',
      'Lower Trapezius – lower scapula region',
      'Rhomboids – between shoulder blades',
      'Latissimus Dorsi – sides of mid-back',
      'Rotator Cuff (Supraspinatus, Infraspinatus, Teres Minor, Subscapularis)',
    ],
    top: '22%',
    left: '74%',
    route: '/pain/upperBack',
  },
  {
    id: 'back-shoulders',
    label: 'Shoulder Region (Back)',
    muscles: ['Deltoid', 'Rotator Cuff Muscles'],
    top: '20%',
    left: '83%',
    route: '/pain/shoulderBack',
  },

  // ==== LOWER BACK ====
  {
    id: 'back-lower-back',
    label: 'Lower Back',
    muscles: [
      'Lumbar Spine – lower back',
      'Quadratus Lumborum (QL) – low back/flank',
      'Erector Spinae – runs along spine',
    ],
    top: '40%',
    left: '70%',
    route: '/pain/lowerBack',
  },

  // ==== PELVIS & HIPS (BACK) ====
  {
    id: 'back-pelvis-hips',
    label: 'Pelvis & Hips (Back)',
    muscles: [
      'Pelvic Floor Muscles – base of pelvis',
      'Hip Joint – upper thigh',
      'Gluteus Maximus – buttocks',
      'Gluteus Medius – outer hip',
      'Gluteus Minimus – deep outer hip',
      'Piriformis – deep gluteal area',
      'IT Band – outer thigh',
    ],
    top: '50%',
    left: '70%',
    route: '/pain/pelvisHipsBack',
  },

  // ==== THIGH (BACK) / HAMSTRINGS ====
  {
    id: 'back-thigh',
    label: 'Back Thigh (Hamstrings)',
    muscles: [
      'Hamstrings (Biceps Femoris, Semitendinosus, Semimembranosus)',
    ],
    top: '58%',
    left: '78%',
    route: '/pain/thighBack',
  },

  // ==== KNEE (BACK) ====
  {
    id: 'back-knee',
    label: 'Knee (Back)',
    muscles: [
      'Posterior knee joint structures',
      'Meniscus – inside knee',
    ],
    top: '68%',
    left: '68%',
    route: '/pain/kneeBack',
  },

  // ==== LOWER LEG (BACK) / CALF ====
  {
    id: 'back-calf',
    label: 'Calf Muscles',
    muscles: ['Gastrocnemius', 'Soleus'],
    top: '75%',
    left: '78%',
    route: '/pain/calf',
  },

  // ==== ANKLE & FOOT (BACK) ====
  {
    id: 'back-ankle-foot',
    label: 'Ankle, Achilles & Foot',
    muscles: [
      'Achilles Tendon – back of ankle',
      'Plantar Fascia – bottom of foot',
      'Foot Arch Muscles',
      'Toes / Intrinsic Foot Muscles',
    ],
    top: '90%',
    left: '69%',
    route: '/pain/ankleFootBack',
  },
];

const BodyImageMap = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('front');

  const regions = view === 'front' ? frontRegions : backRegions;

  const handleRegionClick = (region) => {
    if (region.route) {
      navigate(region.route);
    }
  };

  return (
    <div className={styles.bodyMapWrapper}>
      {/* Front / Back toggle */}
      <div className={styles.toggleRow}>
        <button
          type="button"
          className={`${styles.toggleBtn} ${
            view === 'front' ? styles.toggleActive : ''
          }`}
          onClick={() => setView('front')}
        >
          Front
        </button>
        <button
          type="button"
          className={`${styles.toggleBtn} ${
            view === 'back' ? styles.toggleActive : ''
          }`}
          onClick={() => setView('back')}
        >
          Back
        </button>
      </div>

      {/* Image + overlayed hotspots */}
      <div className={styles.imageContainer}>
        <img
          src="/body-front-back.png"
          alt="Body anatomy front and back"
          className={styles.bodyImage}
        />

        {regions.map((region) => (
          <button
            key={region.id}
            type="button"
            className={`${styles.hotspot} ${
              view === 'back' ? styles.backHotspot : ''
            }`}
            style={{ top: region.top, left: region.left }}
            onClick={() => handleRegionClick(region)}
            aria-label={region.label}
          >
            <span className={styles.hotspotTooltip}>
              <strong>{region.label}</strong>
              {region.muscles && (
                <span className={styles.hotspotSub}>
                  {region.muscles.join(', ')}
                </span>
              )}
            </span>
          </button>
        ))}

        <div className={styles.sideLabel}>
          {view === 'front' ? 'Front View' : 'Back View'}
        </div>
      </div>
    </div>
  );
};

export default BodyImageMap;
